import React, { memo, useCallback, useMemo } from "react";
import TableSkeleton from "../Skeleton/TableSkeleton";
import UtcToLocal from "../UtcToLocal";
import LoadMoreAction from "../Common/LoadMoreAction";
import EmptyState from "../Common/EmptyState";
import ApiErrorState from "../Common/ApiErrorState";
import "./../../Styles/table.scss";

const tableDateOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
};

function isUtcDateValue(value) {
  if (typeof value !== "string") return false;

  const trimmedValue = value.trim();
  if (!trimmedValue) return false;

  const looksLikeIsoDate =
    /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(
      trimmedValue,
    );

  if (!looksLikeIsoDate) return false;

  return !Number.isNaN(Date.parse(trimmedValue));
}

function getValue(row, column) {
  if (typeof column.accessor === "function") return column.accessor(row);
  if (typeof column.accessor === "string") return row?.[column.accessor];
  return undefined;
}

function getColumnIdentity(column) {
  return [
    column?.id,
    typeof column?.header === "string" ? column.header : "",
    typeof column?.accessor === "string" ? column.accessor : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function getColumnKind(column) {
  if (column.kind) return column.kind;

  const identity = getColumnIdentity(column);

  if (
    identity.includes("status") ||
    identity.includes("state")
  ) {
    return "status";
  }

  if (
    identity.includes("action") ||
    identity.includes("details") ||
    identity.includes("view") ||
    identity.includes("menu") ||
    identity.includes("cancel") ||
    identity.includes("reverse") ||
    identity.includes("download qr") ||
    identity.includes("download receipt") ||
    identity.includes("download") ||
    identity.includes("copy code") ||
    identity.includes("proof of payment") ||
    identity.includes("reference")
  ) {
    return "action";
  }

  if (
    identity.includes("date") ||
    identity.includes("time") ||
    identity.includes("timestamp")
  ) {
    return "datetime";
  }

  if (
    identity.includes("amount") ||
    identity.includes("balance") ||
    identity.includes("fees") ||
    identity.includes("commission") ||
    identity.includes("total")
  ) {
    return "amount";
  }

  return "default";
}

function getColumnStyle(column, kind) {
  const style = {};

  if (column.width !== undefined) {
    style.width = column.width;
  }

  if (column.minWidth !== undefined) {
    style.minWidth = column.minWidth;
  }

  if (column.maxWidth !== undefined) {
    style.maxWidth = column.maxWidth;
  }

  if (column.width === undefined && column.minWidth === undefined) {
    if (kind === "status") {
      style.minWidth = "180px";
    } else if (kind === "action") {
      style.minWidth = "110px";
    } else if (kind === "datetime") {
      style.minWidth = "120px";
    } else if (kind === "amount") {
      style.minWidth = "110px";
    }
  }

  if (column.width !== undefined && column.minWidth === undefined) {
    style.minWidth = column.width;
  }

  return style;
}

function renderTableCellValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (isUtcDateValue(value)) {
    return <UtcToLocal utcTimestamp={value} options={tableDateOptions} />;
  }
  return value;
}

function getCellTitle(value, column) {
  if (typeof column.getTitle === "function") {
    return column.getTitle(value);
  }

  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);

  return "";
}

function getCellContentClassName(value, column) {
  return [
    "new-data-table__cell-content",
    column.noTruncate ? "is-no-truncate" : "",
    column.noWrap || isUtcDateValue(value) ? "is-nowrap" : "",
    column.__kind ? `is-${column.__kind}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function getColumnLabel(column) {
  if (column.label !== undefined) return column.label;
  return typeof column.header === "string" ? column.header : "";
}

const DataTableRow = memo(
  function DataTableRow({
    row,
    rowIndex,
    visibleColumns,
    rowClickable,
    onRowClick,
    getRowClassName,
    isActive,
  }) {
    return (
      <tr
        className={[
          rowClickable ? "is-clickable" : "",
          isActive ? "is-active" : "",
          typeof getRowClassName === "function" ? getRowClassName(row, rowIndex) : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={(event) => {
          if (rowClickable) onRowClick?.(row, rowIndex, event);
        }}
      >
        {visibleColumns.map((column) => (
          (() => {
            const value = getValue(row, column);
            const content =
              typeof column.cell === "function"
                ? column.cell({
                    value,
                    row,
                    rowIndex,
                    column,
                    isUtcDateValue,
                    renderTableCellValue,
                  })
                : renderTableCellValue(value);

            const title = getCellTitle(value, column);

            return (
              <td
                key={column.id || column.accessor || column.header}
                className={[
                  column.align ? `is-${column.align}` : "",
                  column.__kind ? `is-${column.__kind}-column` : "",
                  column.cellClassName || column.className || "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                data-label={getColumnLabel(column)}
                style={column.__style}
              >
                <div
                  className={getCellContentClassName(value, column)}
                  title={title || undefined}
                >
                  {content}
                </div>
              </td>
            );
          })()
        ))}
      </tr>
    );
  },
  (prevProps, nextProps) =>
    prevProps.row === nextProps.row &&
    prevProps.rowIndex === nextProps.rowIndex &&
    prevProps.rowKeyValue === nextProps.rowKeyValue &&
    prevProps.visibleColumns === nextProps.visibleColumns &&
    prevProps.rowClickable === nextProps.rowClickable &&
    prevProps.onRowClick === nextProps.onRowClick &&
    prevProps.getRowClassName === nextProps.getRowClassName &&
    prevProps.isActive === nextProps.isActive,
);

function DataTable({
  columns = [],
  data = [],
  loading = false,
  error = null,
  errorMessage = "Unable to load data right now.",
  onRetry,
  emptyMessage = "No data available.",
  showLoadMore = false,
  hasMore = false,
  loadMoreDisabled = false,
  loadMoreLoading = false,
  loadMoreLabel,
  loadMoreLoadingLabel,
  loadMoreSummary,
  loadMoreMinRows = 10,
  loadMoreError = null,
  loadMoreErrorMessage = "Unable to load more data right now.",
  onLoadMoreRetry,
  onLoadMore,
  rowKey,
  onRowClick,
  rowClickable = Boolean(onRowClick),
  getRowClassName,
  activeRowIndex = null,
  activeRowKey = null,
}) {
  const visibleColumns = useMemo(
    () =>
      columns
        .filter((column) => !column.hidden)
        .map((column) => {
          const kind = getColumnKind(column);

          return {
            ...column,
            __kind: kind,
            __style: getColumnStyle(column, kind),
          };
        }),
    [columns],
  );

  const getRowKey = useCallback((row, rowIndex) => {
    if (typeof rowKey === "function") return rowKey(row, rowIndex);
    if (typeof rowKey === "string") return row?.[rowKey] ?? rowIndex;
    return row?.id ?? row?.Transaction ?? row?.Reference ?? rowIndex;
  }, [rowKey]);

  const getIsRowClickable = useCallback(
    (row, rowIndex) =>
      typeof rowClickable === "function"
        ? Boolean(rowClickable(row, rowIndex))
        : Boolean(rowClickable),
    [rowClickable],
  );

  const hasError = Boolean(error);
  const hasLoadMoreError = Boolean(loadMoreError) && data.length > 0;
  const canRenderLoadMore =
    showLoadMore &&
    hasMore &&
    data.length >= loadMoreMinRows &&
    visibleColumns.length > 0;
  const resolvedErrorMessage =
    typeof error === "string"
      ? error
      : error?.message || errorMessage || "Unable to load data right now.";
  const resolvedLoadMoreErrorMessage =
    typeof loadMoreError === "string"
      ? loadMoreError
      : loadMoreError?.message ||
        loadMoreErrorMessage ||
        "Unable to load more data right now.";

  return (
    <section className="new-data-table">
      <div className="new-data-table__scroll">
        <table>
          <colgroup>
            {visibleColumns.map((column) => (
              <col
                key={column.id || column.accessor || column.header}
                style={column.__style}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.id || column.accessor || column.header}
                  className={[
                    column.align ? `is-${column.align}` : "",
                    column.__kind ? `is-${column.__kind}-column` : "",
                    column.headerClassName || column.className || "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={column.__style}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableSkeleton columns={visibleColumns} />
            ) : hasError ? (
              <tr className="new-data-table__state-row">
                <td colSpan={Math.max(visibleColumns.length, 1)}>
                  <ApiErrorState
                    compact
                    message={resolvedErrorMessage}
                    onRetry={onRetry}
                  />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr className="new-data-table__state-row">
                <td colSpan={Math.max(visibleColumns.length, 1)}>
                  <EmptyState compact message={emptyMessage} />
                </td>
              </tr>
            ) : (
              <>
                {data.map((row, rowIndex) => {
                  const currentRowKey = getRowKey(row, rowIndex);
                  const isActiveByIndex =
                    Number.isInteger(activeRowIndex) && rowIndex === activeRowIndex;
                  const isActiveByKey =
                    activeRowKey !== null &&
                    activeRowKey !== undefined &&
                    String(currentRowKey) === String(activeRowKey);

                  return (
                    <DataTableRow
                      key={currentRowKey}
                      row={row}
                      rowIndex={rowIndex}
                      rowKeyValue={currentRowKey}
                      visibleColumns={visibleColumns}
                      rowClickable={getIsRowClickable(row, rowIndex)}
                      onRowClick={onRowClick}
                      getRowClassName={getRowClassName}
                      isActive={isActiveByIndex || isActiveByKey}
                    />
                  );
                })}

                {hasLoadMoreError && visibleColumns.length > 0 ? (
                  <tr className="new-data-table__state-row">
                    <td colSpan={visibleColumns.length}>
                      <ApiErrorState
                        compact
                        message={resolvedLoadMoreErrorMessage}
                        onRetry={onLoadMoreRetry || onLoadMore}
                      />
                    </td>
                  </tr>
                ) : canRenderLoadMore ? (
                  <tr className="new-data-table__load-more-row">
                    <td colSpan={visibleColumns.length}>
                      <LoadMoreAction
                        className="new-data-table__load-more"
                        canLoadMore={hasMore}
                        disabled={loadMoreDisabled}
                        loading={loadMoreLoading}
                        label={loadMoreLabel}
                        loadingLabel={loadMoreLoadingLabel}
                        summary={loadMoreSummary}
                        onClick={onLoadMore}
                      />
                    </td>
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default memo(DataTable);
